import { Component, Inject, OnInit, PLATFORM_ID, ChangeDetectionStrategy } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { RxFormBuilder } from '@rxweb/reactive-form-validators';
import { takeUntil } from 'rxjs';
import { BaseComponent } from 'src/app/core/base-classes/base/base.component';
import { FactorSetting } from 'src/app/shared/models/auth/factor-setting.modal';
import { User } from 'src/app/shared/models/auth/user.model';
import { Company } from 'src/app/shared/models/company.model';
import { fadeInUpAnimation } from '../../shared/animations/fade-in-up.animation';
import { GlobalService } from 'src/app/shared/services/global.service';
import { ApplicationNameService } from 'src/app/shared/services/application-name.service';
import { AuthService } from 'src/app/shared/services/auth.service';
import { ApiResponse } from 'src/app/shared/models/api.response';
import { environment } from 'src/environments/environment';
import { SnackbarService } from 'src/app/shared/services/snackBarService.service';
import { isPlatformBrowser } from '@angular/common';
import { CloudProviderState } from 'src/app/shared/enums/cloudprovider-state.enum';
import { CompanyApiService } from 'src/app/company/services/company-api.service';

@Component({
    selector: 'app-login',
    templateUrl: './login.component.html',
    styleUrls: ['./login.component.scss'],
    animations: [fadeInUpAnimation],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class LoginComponent extends BaseComponent implements OnInit {
  public copyrightYear = new Date().getFullYear();
  public loginForm: FormGroup;
  public submitted: boolean = false;
  public user: User;
  public userRef: User;
  public company: Company;
  public isSubmitted: boolean = false;
  public isPhone: boolean = false;
  public isPass: boolean = false;
  public isNew: boolean = false;
  public isActivate: boolean = false;
  public isLoading: boolean = true;
  public factor: FactorSetting;
  public factorForm: FormGroup;
  public otpSent: boolean = false;
  public display: string;
  public isButtonDisabled: boolean = true;
  public hide: boolean = true;
  public hideOtp: boolean = true;
  public returnUrl: string;
  public CloudProviderState: typeof CloudProviderState = CloudProviderState;

  get isAdmin(): boolean {
    return this.user?.userType?.toLowerCase() === 'admin';
  }

  get isReviewer(): boolean {
    return this.user?.userType?.toLowerCase() === 'reviewer';
  }

  constructor(
    private _formBuilder: RxFormBuilder,
    private _router: Router,
    private _activatedRoute: ActivatedRoute,
    private _companyService: CompanyApiService,
    private _globalService: GlobalService,
    public applicationNameService: ApplicationNameService,
    private _authService: AuthService,
    @Inject(PLATFORM_ID) private _platformId: unknown,
    private _snackbarService: SnackbarService
  ) {
    super();
    this.factor = new FactorSetting();
    this.user = new User();
    this.userRef = new User();
    this.company = new Company();
  }

  ngOnInit(): void {
    this.loginForm = this._formBuilder.formGroup(User);
    this.factorForm = this._formBuilder.formGroup(FactorSetting);
    if (!environment.production) {
      this.company.companyName = 'msh';
      this._companyService.companyDetail(this.company.companyName).pipe(takeUntil(this.destroy$)).subscribe((res: ApiResponse<Company>) => {
        this.company = res.data;
        this._globalService.setCompany('company', JSON.stringify(this.company));
      });
      this.isLoading = false;
      return;
    }

    if (isPlatformBrowser(this._platformId)) {
      const companyName = window.location.origin.split('.');
      this.company.companyName = companyName[0].split(':')[1].replace('//', '');
      this.returnUrl = this._activatedRoute.snapshot.queryParamMap.get('returnUrl') || '';
      if (this.company.companyName == 'console') {
        this._router.navigateByUrl('/company');
      } else {
        this._companyService.companyDetail(this.company.companyName).pipe(takeUntil(this.destroy$)).subscribe((res: ApiResponse<Company>) => {
          this.company = res.data;
          this._globalService.setCompany('company', JSON.stringify(this.company));

          if (this._authService.isAuthenticated()) {
            this.isLoading = false;
            if (this._globalService.user.factorStatus == 'Pending') {
              this.isNew = true;
            } else {
              this._router.navigateByUrl('/dashboard');
            }
          } else {
            if (this.company.ssoEnable && this.company.companyName != 'console') {
              if (this.company.ssoType == 'saml') {
                if (this.company.cloudProvider == this.CloudProviderState.Okta) {
                  window.location.href = environment.samlLoginUrl + this.company.entityId;
                } else if (this.company.cloudProvider == this.CloudProviderState.JumpCloud) {
                  window.location.href = environment.jumpCloudUrl + this.company.accessId;
                } else if (this.company.cloudProvider == this.CloudProviderState.PingOne) {
                  window.location.href = environment.pingOneSamlUrl + this.company.accessId;
                } else {
                  window.location.href = environment.samlAzureUrl + this.company.accessId;
                }
              } else {
                let redirectUrl = environment.ssoRedirectUrl;
                redirectUrl = redirectUrl.replace(
                  redirectUrl.split('//')[1].split('.')[0],
                  this.company.companyName
                );
                const loginUrl =
                  this.company.url +
                  '/oauth2/default/v1/authorize?scope=openid+email+profile&redirect_uri=' +
                  redirectUrl +
                  '&response_type=token id_token&client_id=' +
                  this.company.clientId +
                  '&nonce=' +
                  Math.random().toString(13).replace('0.', '') +
                  '&state=eyJjc3JmX3Rva2VuIjogIkNhZG1';
                window.location.href = loginUrl;
              }
            } else {
              if (this.company.companyName == 'console') {
                this._router.navigateByUrl('/company');
              }
              this.isLoading = false;
              this._activatedRoute.queryParams.pipe(takeUntil(this.destroy$)).subscribe((params) => {
                if (params['email']) {
                  this.user.name = params['email'];
                  this.checkUserType();
                }
              });
            }
          }
        },
          () => {
            setTimeout(() => {
              window.location.href = environment.errorRedirect;
            }, 500);
          }
        );
      }
    }
  }

  public userLogin(): void {
    this.isSubmitted = true;
    const firstName = this.loginForm.get('firstName') as FormControl;
    firstName.clearValidators();
    firstName.updateValueAndValidity();
    const lastName = this.loginForm.get('lastName') as FormControl;
    lastName.clearValidators();
    lastName.updateValueAndValidity();
    const userType = this.loginForm.get('userType') as FormControl;
    userType.clearValidators();
    userType.updateValueAndValidity();

    if (this.loginForm.invalid) {
      return;
    }

    this.user.email = this.loginForm.get('email').value || this.user.email;
    this.user.password = this.loginForm.get('password').value || this.user.password;
    this.user.accessId = this.company?.accessId;
    this.user.companyId = this.company.id;
    this._authService.login(this.user).pipe(takeUntil(this.destroy$)).subscribe((res: ApiResponse<User>) => {

      if (Object.entries(res.data).length > 0) {
        this.userRef = res.data;
        this._globalService.user = this.userRef;
        if (this._globalService.user.factorType) {
          this.userRef.factorStatus = 'Pending';
        }
        if (this._globalService.user.factorType) {
          if (this._globalService.user.factorId) {
            this.isActivate = true;
            this.isNew = true;

            this.factor.factorType = this._globalService.user.factorType;
            this.factor.factorId = this._globalService.user.factorId;
            this.factor.userUuid = this._globalService.user.uuid;
            this.factor.phoneNumber =
              this._globalService.user.factorPhone?.replace(/.(?=.{4})/g, '*');
            this._authService
              .factorChallenege(this.factor)
              .pipe(takeUntil(this.destroy$))
              .subscribe(() => {
                this.isActivate = true;
              });
          } else if (this._globalService.user.factorPhone) {
            this.factor.phoneNumber = this._globalService.user.factorPhone;
            this.addFactor();
          } else {
            this.isNew = true;
          }
        } else {
          this._snackbarService.open(res.message, 'Close', 'success');
          this._globalService.setUser('userData', JSON.stringify(this.userRef));
          if (this.userRef.userType == 'Requestor')
            this._router.navigateByUrl('/access-request/submitted-requests');
          else this._router.navigateByUrl(this.returnUrl);
        }
      }
    });
  }

  public checkUserType(): void {
    if (this.loginForm.get('email').invalid) return;
    this.user.email = this.loginForm.get('email').value || this.user.email;
    this._authService.checkUserType({ companyId: this._globalService.company.id, email: this.user.email })
      .pipe(takeUntil(this.destroy$))
      .subscribe((res) => {
        const response = res as ApiResponse<string>;
        this.user.userType = response.data;
        this.otpSent = true;
        this.isButtonDisabled = true;
        if (this.user.userType == 'Reviewer') {
          this.timer();
        }
      });
  }

  public addFactor(): void {
    this.isPhone = true;
    const factorType = this.factorForm.controls['factorType'] as FormControl;
    factorType.clearValidators();
    factorType.updateValueAndValidity();
    const passCode = this.factorForm.controls['passCode'] as FormControl;
    passCode.clearValidators();
    passCode.updateValueAndValidity();
    this.factor.userUuid = this._globalService.user.uuid;
    this.factor.factorType = this._globalService.user.factorType;
    this.factor.userUuid = this._globalService.user.uuid;
    this.factor.companyId = this._globalService.company.id;
    if (this.factorForm.invalid) return;
    const phoneVal = this.factorForm.get('phoneNumber')?.value || this.factor.phoneNumber || '';
    if (phoneVal.substring(0, 2) != '+1') {
      this.factor.phoneNumber = `+1${phoneVal}`;
    } else {
      this.factor.phoneNumber = phoneVal;
    }

    this._authService.enrollFactor(this.factor).pipe(takeUntil(this.destroy$)).subscribe((res) => {
      const response = res as ApiResponse<{ id: number }>;
      this.isActivate = true;
      this.factor.factorId = response.data.id;
      passCode.setValidators([Validators.required]);
      passCode.updateValueAndValidity();
    });
  }

  public activateFactor(): void {
    this.isPass = true;
    const factorType = this.factorForm.controls['factorType'] as FormControl;
    factorType.clearValidators();
    factorType.updateValueAndValidity();
    const phoneNumber = this.factorForm.controls['phoneNumber'] as FormControl;
    phoneNumber.clearValidators();
    phoneNumber.updateValueAndValidity();
    this.factor.phoneNumber = this._globalService.user.factorType;
    if (this.factorForm.invalid) return;
    this.factor.passCode = this.factorForm.get('passCode')?.value || this.factor.passCode;
    if (this._globalService.user.factorId) {
      this._authService.verifyFactor(this.factor).pipe(takeUntil(this.destroy$)).subscribe((res) => {
        const response = res as ApiResponse<{ factorResult: string }>;
        if (response.data.factorResult == 'SUCCESS') {
          const user = this._globalService.user;
          user.factorStatus = 'ACTIVE';
          this._globalService.setUser('userData', JSON.stringify(user));
          this.loginRedirect();
        }
      });
    } else {
      this._authService.activateFactor(this.factor).pipe(takeUntil(this.destroy$)).subscribe((res) => {
        const response = res as ApiResponse<{ status: string }>;
        if (response.data.status == 'ACTIVE') {
          const user = this._globalService.user;
          user.factorStatus = response.data.status;
          this._globalService.setUser('userData', JSON.stringify(user));
          this.loginRedirect();
        }
      });
    }
  }

  public loginRedirect(): void {
    this._snackbarService.open('Logged in successfully.', 'Close', 'success');
    if (this.user.userType == 'Requestor')
      this._router.navigateByUrl('/access-request/submitted-requests');
    else this._router.navigateByUrl('/');
  }

  public timer(): void {
    let timeleft = 60;
    const timer = setInterval(() => {
      timeleft--;
      if (timeleft <= 0) {
        this.isButtonDisabled = false;
        clearInterval(timer);
        document.getElementById("countdown").innerHTML = "00:00";
      } else {
        document.getElementById("countdown").innerHTML = "00:" + timeleft.toString().padStart(2, "0");
      }
    }, 1000);
  }
}
